import { wrapApiCall } from '@/lib/axios'
import { clampTotalPages, fetchPagesInBatches } from '@/utils/helper/paged-fetch.helper'
import type { AxiosInstance } from 'axios'

const BASE_PATH = '/postgrest'
const POSTGREST_PAGE_SIZE = 500

const TAX_SUMMARY_SELECT = [
  'sold_date_local',
  'tax_invoice_no_range',
  'item_count',
  'full_amount_thb',
  'vat_base_thb',
  'tax_amount_thb',
].join(',')

const TAX_REPORT_SELECT = [
  'receipt_id',
  'sold_at',
  'sold_date_local',
  'tax_invoice_no',
  'pos_id',
  'member_name',
  'is_voided',
  'item_count',
  'full_amount_thb',
  'vat_base_thb',
  'vat_amount_thb',
].join(',')

const TAX_INVOICE_REQUEST_SELECT = [
  'receipt_id',
  'sold_at',
  'sold_date_local',
  'invoice_no',
  'full_name',
  'company_name',
  'branch_no',
  'email_address',
  'tax_id',
  'address',
  'tax_sub_district_id',
  'tax_district_id',
  'tax_province_id',
  'tax_address_line',
  'full_amount_thb',
  'vat_base_thb',
  'vat_amount_thb',
].join(',')

const CREDIT_REPORT_SELECT = [
  'credit_tx_id',
  'sold_at',
  'sold_date_local',
  'member_id',
  'member_code',
  'member_name',
  'product_name',
  'pt_name',
  'sub_id',
  'earned_credits',
  'used_credits',
  'remaining_credits',
  'vat_rate',
].join(',')

const CREDIT_CONVERTED_REPORT_SELECT = [
  'credit_tx_id',
  'sold_at',
  'sold_date_local',
  'sold_time_local',
  'member_id',
  'member_code',
  'member_name',
  'product_name',
  'pt_name',
  'sub_id',
  'revenue_type',
  'branch_name',
  'staff_name',
  'reference_id',
  'used_credits',
  'full_amount_thb',
  'vat_base_thb',
  'vat_amount_thb',
  'total_amount_thb',
  'vat_rate',
].join(',')

type PaginationPayload = {
  total?: number | string
  page?: number
  pageSize?: number
}

type PostgrestRowsResponse<T> = {
  data?: T[]
  pagination?: PaginationPayload
}

export type SaleFactRow = {
  receipt_item_id: number
  receipt_id: number
  sold_at: string
  sold_date_local: string
  sold_time_local: string
  member_code: string | null
  member_full_name: string | null
  item_name: string
  sub_id: string
  quantity: number | string
  unit_price_thb: number | string
  full_amount_thb: number | string
  discount_thb: number | string
  vat_thb: number | string
  net_amount_thb: number | string
  credit_amount: number | string
  line_total_thb: number | string
  payment_method_label: string
  payment_method_key: string
  is_installment: boolean
  card_payment_type: string
  fee_thb: number | string
  seller_code: string | null
  seller_full_name: string | null
  department: string | null
  sample_tax_invoice_id: string | null
  remark: string | null
  sales_category: string
  // Added by the Sale Report depth work; the row mappers show a dash when one
  // is missing.
  seller_user_id?: number | string | null
  receipt_code?: string | null
  branch_name?: string | null
  // Membership-backed lines only; products and bookings have no package.
  package_name?: string | null
  package_type_name_en?: string | null
  // Card payments only.
  bank_name?: string | null
  installment_period?: number | string | null
  seller_role_name?: string | null
  // Purchase source at sale time; also null on lines without a membership
  // (products, bookings, credits).
  purchase_source?: SaleFactPurchaseSource | null
  // Giveaway lines. Null on the same rows purchase_source is null on, so a
  // blank cell never reads as "sold, not given away".
  is_complimentary?: boolean | null
}

export type SaleFactPurchaseSource = 'NEW' | 'EXISTING'

// The groupings behind the Sale Summary charts, read live off
// GET /accounting-and-tax/sale-summary and summed over the whole picked range
// (the charts never split by day). The `*_thb` columns are SATANG despite the
// suffix (convert with `satangToBaht`), and numerics arrive as strings.

/** Sales per category × payment label. */
export type SaleDashboardSummaryRow = {
  sales_category: string
  payment_method_key: string
  payment_method_label: string
  card_payment_type: string
  line_count: number | string
  receipt_count: number | string
  total_full_amount_thb: number | string
  total_discount_thb: number | string
  total_vat_thb: number | string
  total_net_amount_thb: number | string
  total_line_total_thb: number | string
  total_fee_thb: number | string
}

/** Sales per seller × chart item (the by-seller and Top-12 charts). */
export type SaleDashboardTop12Row = {
  sales_category: string
  seller_user_id: number | string | null
  seller_full_name: string | null
  department: string | null
  chart_item_name: string
  line_count: number | string
  receipt_count: number | string
  total_full_amount_thb: number | string
  total_discount_thb: number | string
  total_vat_thb: number | string
  total_net_amount_thb: number | string
  total_line_total_thb: number | string
}

export type SaleSummaryResult = {
  summaryRows: SaleDashboardSummaryRow[]
  topRows: SaleDashboardTop12Row[]
}

export type TaxSummaryRow = {
  sold_date_local: string
  tax_invoice_no_range: string | null
  item_count: number | string
  full_amount_thb: number | string
  // Read off the receipts as issued (receipts.total − receipts.vat, and
  // receipts.vat), so a non-VAT club shows VAT 0.
  vat_base_thb: number | string
  tax_amount_thb: number | string
}

export type TaxReportRow = {
  receipt_id: number
  sold_at: string
  sold_date_local: string
  tax_invoice_no: string | null
  pos_id: string | null
  member_name: string | null
  // Voided receipts stay in the sales tax report with zero amounts; the view
  // flags them so the table can show the "ยกเลิก" remark.
  is_voided?: boolean | null
  item_count: number | string
  full_amount_thb: number | string
  vat_base_thb: number | string
  vat_amount_thb: number | string
}

export type TaxInvoiceRequestRow = {
  receipt_id: number
  sold_at: string
  sold_date_local: string
  invoice_no: string | null
  full_name: string | null
  company_name: string | null
  branch_no: string | null
  email_address: string | null
  tax_id: string | null
  address: string | null
  tax_sub_district_id: number | null
  tax_district_id: number | null
  tax_province_id: number | null
  tax_address_line: string | null
  full_amount_thb: number | string
  vat_base_thb: number | string
  vat_amount_thb: number | string
}

export type SubDistrictDetail = {
  subDistrictId: number
  subDistrictNameTh: string
  subDistrictNameEn: string
  districtId: number
  districtNameTh: string
  districtNameEn: string
  provinceId: number
  provinceNameTh: string
  provinceNameEn: string
  postalCode: string
}

export type CreditReportRow = {
  credit_tx_id: number
  sold_at: string
  sold_date_local: string
  member_id: number
  member_code: string | null
  member_name: string | null
  product_name: string | null
  pt_name: string | null
  sub_id: string | null
  earned_credits: number | string
  used_credits: number | string
  remaining_credits: number | string
  // The club's effective VAT rate (0 when not VAT-registered).
  vat_rate: number | string
}

export type CreditConvertedReportRow = {
  credit_tx_id: number
  sold_at: string
  sold_date_local: string
  sold_time_local: string | null
  member_id: number
  member_code: string | null
  member_name: string | null
  product_name: string | null
  pt_name: string | null
  sub_id: string | null
  revenue_type: string | null
  branch_name: string | null
  staff_name: string | null
  reference_id: string | null
  used_credits: number | string
  full_amount_thb: number | string
  vat_base_thb: number | string
  vat_amount_thb: number | string
  total_amount_thb: number | string
  vat_rate: number | string
}

export type QuotaReportRow = {
  quotaLogId: number
  soldAt: string
  soldDateLocal: string
  soldTimeLocal: string | null
  memberCode: string | null
  memberName: string | null
  productName: string | null
  subId: string | null
  quotaType: string | null
  isUnlimit: boolean
  totalQuota: number | string | null
  usedQuota: number | string | null
  remainingQuota: number | string | null
  // Detail columns; optional so the table still renders against a backend
  // that predates them (the API deploys before the frontend).
  packageTypeNameEn?: string | null
  packageTypeNameTh?: string | null
  branchName?: string | null
  /** Staff member who recorded the deduction. */
  actorName?: string | null
  /** CLASS / PT / SWIMMING / FACILITY / UNKNOWN. */
  usageType?: string | null
  userMembershipId?: number | null
}

export type MembershipExpiredReportRow = {
  quotaLogId: number
  soldAt: string
  soldDateLocal: string
  memberCode: string | null
  memberName: string | null
  /** The package name; kept under the old field name for compatibility. */
  quotaType: string | null
  membershipExpireDate: string | null
  isUnlimit: boolean
  totalQuota: number | string | null
  usedQuota: number | string | null
  remainingQuota: number | string | null
  // Detail columns; optional so the table still renders against a backend
  // that predates them (the API deploys before the frontend).
  userMembershipId?: number | null
  packageName?: string | null
  packageTypeNameEn?: string | null
  packageTypeNameTh?: string | null
  subId?: string | null
  startDate?: string | null
  purchasedAt?: string | null
  purchaseSource?: string | null
  saleName?: string | null
  branchName?: string | null
  memberPhone?: string | null
  memberEmail?: string | null
  memberId?: number | null
  lastCheckInAt?: string | null
  /** Days SINCE expiry: negative while the membership is still running. The
   * table flips the sign and shows it as "Days to Expiry". */
  daysSinceExpired?: number | string | null
  isActive?: boolean | null
}

type DateRangeInput = {
  fromDate: string
  toDate: string
}

type SaleReportFilterInput = {
  categories?: string[]
  /** sales_fact_view.seller_user_id values. */
  sellerIds?: number[]
  /** sales_fact_view.payment_method_key values (CASH / CARD / TRANSFER). */
  paymentMethods?: string[]
  /** NEW / EXISTING; drops lines with no membership once set. */
  purchaseSources?: SaleFactPurchaseSource[]
  /**
   * true = giveaways only, false = everything else (products, bookings and
   * credits included — they carry no flag at all), undefined = both.
   */
  isComplimentary?: boolean
}

type SaleFactInput = DateRangeInput &
  SaleReportFilterInput & {
    page: number
    pageSize: number
  }

export type SaleFactRowsResult = {
  rows: SaleFactRow[]
  total: number
}

type SaleReportDownloadInput = DateRangeInput & SaleReportFilterInput

// Query params shared by the Sale Report table and its Excel, so the two can
// never filter differently. Each filter is omitted when empty, which the
// backend reads as "no filter".
const saleReportParams = (input: SaleReportDownloadInput) => ({
  startDate: input.fromDate,
  endDate: input.toDate,
  ...(input.categories?.length ? { categories: input.categories.join(',') } : {}),
  ...(input.sellerIds?.length ? { sellerIds: input.sellerIds.join(',') } : {}),
  ...(input.paymentMethods?.length ? { paymentMethods: input.paymentMethods.join(',') } : {}),
  ...(input.purchaseSources?.length ? { purchaseSources: input.purchaseSources.join(',') } : {}),
  ...(input.isComplimentary !== undefined ? { isComplimentary: input.isComplimentary } : {}),
})

type ReportRowsInput = DateRangeInput & {
  page: number
  pageSize: number
}

const appendDateFilters = (query: URLSearchParams, input: DateRangeInput) => {
  query.append('filter', `sold_date_local=gte.${input.fromDate}`)
  query.append('filter', `sold_date_local=lte.${input.toDate}`)
}

const toTotal = (payload?: PaginationPayload, fallback = 0): number => {
  const raw = payload?.total
  if (typeof raw === 'number') return raw
  if (typeof raw === 'string') {
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

export const AccountingAndTaxService = (client: AxiosInstance) => {
  const buildFetchAllQuery = ({
    select,
    page,
    order,
    input,
    withCount,
  }: {
    select: string
    page: number
    order?: string
    input: DateRangeInput
    withCount: boolean
  }) => {
    const query = new URLSearchParams()
    query.append('select', select)
    if (withCount) query.append('count', 'true')
    query.append('page', String(page))
    query.append('pageSize', String(POSTGREST_PAGE_SIZE))
    if (order) query.append('order', order)
    appendDateFilters(query, input)
    return query
  }

  const fetchAllRows = async <T>({
    resource,
    select,
    order,
    input,
  }: {
    resource: string
    select: string
    order?: string
    input: DateRangeInput
  }): Promise<T[]> => {
    const fetchPage = (page: number, withCount: boolean) =>
      wrapApiCall(() =>
        client.get<PostgrestRowsResponse<T>>(
          `${BASE_PATH}/${resource}?${buildFetchAllQuery({ select, page, order, input, withCount }).toString()}`
        )
      )

    // Page 1 requests the count so we know the total up front; the remaining
    // pages skip `count=true` (the backend would otherwise re-run the full
    // view just to recompute an unchanging total) and are fetched in
    // parallel instead of sequentially, since the total is already known.
    const { data: firstData } = await fetchPage(1, true)
    const firstRows = firstData?.data ?? []

    const total = toTotal(firstData?.pagination, firstRows.length)
    const totalPages = clampTotalPages(
      total,
      POSTGREST_PAGE_SIZE,
      'accounting-and-tax.fetchAllRows'
    )

    if (totalPages <= 1) {
      return firstRows
    }

    const restPages = await fetchPagesInBatches(
      Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
      (page) => fetchPage(page, false).then(({ data }) => data?.data ?? [])
    )

    return [firstRows, ...restPages].flat()
  }

  const fetchPagedRows = async <T>({
    resource,
    select,
    order,
    filters,
    input,
  }: {
    resource: string
    select: string
    order?: string
    filters?: string[]
    input: ReportRowsInput
  }): Promise<{ rows: T[]; total: number }> => {
    const query = new URLSearchParams()
    query.append('select', select)
    query.append('count', 'true')
    query.append('page', String(input.page))
    query.append('pageSize', String(input.pageSize))
    if (order) query.append('order', order)
    appendDateFilters(query, input)
    filters?.forEach((filter) => query.append('filter', filter))

    const { data } = await wrapApiCall(() =>
      client.get<PostgrestRowsResponse<T>>(`${BASE_PATH}/${resource}?${query.toString()}`)
    )

    const rows = data?.data ?? []
    const total = toTotal(data?.pagination, rows.length)
    return { rows, total }
  }

  return {
    // Live rows, not the hourly sales_fact_view matview, so a sale or a void
    // shows up at once. Same shape as quota-report: { data, pagination }.
    async getSaleFactRows(input: SaleFactInput): Promise<SaleFactRowsResult> {
      const { data } = await wrapApiCall(() =>
        client.get<PostgrestRowsResponse<SaleFactRow>>('/accounting-and-tax/report', {
          params: { ...saleReportParams(input), page: input.page, pageSize: input.pageSize },
        })
      )

      const rows = data?.data ?? []
      return { rows, total: toTotal(data?.pagination, rows.length) }
    },

    async downloadSaleReportExcel(input: SaleReportDownloadInput): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/accounting-and-tax/report/excel', {
          params: saleReportParams(input),
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )

      return data
    },

    async getTaxSummaryRows(
      input: ReportRowsInput
    ): Promise<{ rows: TaxSummaryRow[]; total: number }> {
      return fetchPagedRows<TaxSummaryRow>({
        resource: 'tax_summary_view',
        select: TAX_SUMMARY_SELECT,
        order: 'sold_date_local.desc',
        input,
      })
    },

    async getAllTaxSummaryRows(input: DateRangeInput): Promise<TaxSummaryRow[]> {
      return fetchAllRows<TaxSummaryRow>({
        resource: 'tax_summary_view',
        select: TAX_SUMMARY_SELECT,
        order: 'sold_date_local.desc',
        input,
      })
    },

    async getTaxReportRows(
      input: ReportRowsInput
    ): Promise<{ rows: TaxReportRow[]; total: number }> {
      return fetchPagedRows<TaxReportRow>({
        resource: 'tax_report_view',
        select: TAX_REPORT_SELECT,
        order: 'sold_at.desc',
        input,
      })
    },

    async getTaxInvoiceRequestRows(
      input: ReportRowsInput
    ): Promise<{ rows: TaxInvoiceRequestRow[]; total: number }> {
      const result = await fetchPagedRows<TaxInvoiceRequestRow>({
        resource: 'tax_invoice_request_view',
        select: TAX_INVOICE_REQUEST_SELECT,
        order: 'sold_at.desc',
        filters: ['tax_id=is.not_null', 'tax_id=neq.-'],
        input,
      })

      const subDistrictIds = Array.from(
        new Set(
          result.rows
            .map((row) => row.tax_sub_district_id)
            .filter((id): id is number => typeof id === 'number' && id > 0)
        )
      )
      if (subDistrictIds.length === 0) return result

      const { data: details } = await wrapApiCall(() =>
        client.get<SubDistrictDetail[]>('/locations/detail-list', {
          params: { subDistrictIds: subDistrictIds.join(',') },
        })
      )
      const detailMap = new Map<number, SubDistrictDetail>(
        (details ?? []).map((d) => [d.subDistrictId, d])
      )

      const enriched = result.rows.map((row) => {
        const id = row.tax_sub_district_id
        if (id == null) return row
        const detail = detailMap.get(id)
        if (!detail) return row
        const line = (row.tax_address_line ?? '').trim()
        const locationPart = [
          detail.subDistrictNameTh,
          detail.districtNameTh,
          detail.provinceNameTh,
          detail.postalCode,
        ]
          .filter((s) => s && s.trim().length > 0)
          .join(', ')
        const merged = [line, locationPart].filter((s) => s.length > 0).join(', ')
        return { ...row, address: merged.length > 0 ? merged : row.address }
      })

      return { ...result, rows: enriched }
    },

    async getCreditReportRows(
      input: ReportRowsInput
    ): Promise<{ rows: CreditReportRow[]; total: number }> {
      return fetchPagedRows<CreditReportRow>({
        resource: 'credit_report_view',
        select: CREDIT_REPORT_SELECT,
        order: 'sold_at.desc,credit_tx_id.desc',
        input,
      })
    },

    async getCreditConvertedReportRows(
      input: ReportRowsInput
    ): Promise<{ rows: CreditConvertedReportRow[]; total: number }> {
      return fetchPagedRows<CreditConvertedReportRow>({
        resource: 'credit_converted_report_view',
        select: CREDIT_CONVERTED_REPORT_SELECT,
        order: 'sold_at.desc,credit_tx_id.desc',
        filters: ['revenue_type=eq.Used'],
        input,
      })
    },

    async getQuotaReportRows(
      input: ReportRowsInput
    ): Promise<{ rows: QuotaReportRow[]; total: number }> {
      const { data } = await wrapApiCall(() =>
        client.get<PostgrestRowsResponse<QuotaReportRow>>('/accounting-and-tax/quota-report', {
          params: {
            startDate: input.fromDate,
            endDate: input.toDate,
            page: input.page,
            pageSize: input.pageSize,
          },
        })
      )

      const rows = data?.data ?? []
      const total = toTotal(data?.pagination, rows.length)
      return { rows, total }
    },

    async getMembershipExpiredReportRows(
      input: ReportRowsInput
    ): Promise<{ rows: MembershipExpiredReportRow[]; total: number }> {
      const { data } = await wrapApiCall(() =>
        client.get<PostgrestRowsResponse<MembershipExpiredReportRow>>(
          '/accounting-and-tax/membership-expired-report',
          {
            params: {
              startDate: input.fromDate,
              endDate: input.toDate,
              page: input.page,
              pageSize: input.pageSize,
            },
          }
        )
      )

      const rows = data?.data ?? []
      const total = toTotal(data?.pagination, rows.length)
      return { rows, total }
    },

    async downloadTaxSummaryExcel(input: DateRangeInput): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/accounting-and-tax/tax-summary/report/excel', {
          params: {
            startDate: input.fromDate,
            endDate: input.toDate,
          },
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )

      return data
    },

    async downloadTaxReportExcel(input: DateRangeInput): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/accounting-and-tax/tax-report/report/excel', {
          params: {
            startDate: input.fromDate,
            endDate: input.toDate,
          },
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )

      return data
    },

    async downloadTaxInvoiceRequestExcel(input: DateRangeInput): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/accounting-and-tax/tax-invoice-request/report/excel', {
          params: {
            startDate: input.fromDate,
            endDate: input.toDate,
          },
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )

      return data
    },

    async downloadCreditReportExcel(input: DateRangeInput): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/accounting-and-tax/credit-report/report/excel', {
          params: {
            startDate: input.fromDate,
            endDate: input.toDate,
          },
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )

      return data
    },

    async downloadCreditConvertedReportExcel(input: DateRangeInput): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/accounting-and-tax/credit-converted-report/report/excel', {
          params: {
            startDate: input.fromDate,
            endDate: input.toDate,
          },
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )

      return data
    },

    async downloadQuotaReportExcel(input: DateRangeInput): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/accounting-and-tax/quota-report/report/excel', {
          params: {
            startDate: input.fromDate,
            endDate: input.toDate,
          },
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )

      return data
    },

    /** The export stops at 10,000 rows and says so in X-Export-Total / X-Export-Truncated. */
    async downloadMembershipExpiredReportExcel(
      input: DateRangeInput
    ): Promise<{ blob: Blob; total: number; truncated: boolean }> {
      const response = await wrapApiCall(() =>
        client.get<Blob>('/accounting-and-tax/membership-expired-report/report/excel', {
          params: {
            startDate: input.fromDate,
            endDate: input.toDate,
          },
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )

      return {
        blob: response.data,
        total: Number(response.headers['x-export-total'] ?? 0),
        truncated: response.headers['x-export-truncated'] === 'true',
      }
    },

    // Live, like the Sale Report table on the next tab: the hourly
    // sales_dashboard_* matviews it replaces left a sale or a void off the
    // charts for up to an hour. Branch from the branch-id header.
    async getSaleSummary(input: DateRangeInput): Promise<SaleSummaryResult> {
      const { data } = await wrapApiCall(() =>
        client.get<Partial<SaleSummaryResult>>('/accounting-and-tax/sale-summary', {
          params: { startDate: input.fromDate, endDate: input.toDate },
        })
      )

      return {
        summaryRows: data?.summaryRows ?? [],
        topRows: data?.topRows ?? [],
      }
    },
  }
}
